import { Injectable } from '@angular/core';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { Chance } from 'chance';
import _ from 'lodash';
import move from 'lodash-move';
import { ToolbarSettingRemoteDto } from 'src/generated/swagger/model/toolbarSettingRemoteDto';
import { UserActionRemoteDto, UserActionSettingBaseRemoteDto } from '../../../../../../../generated/swagger';
import { PresentationType } from '../../../../../../samwebDescription/samweb/settings/presentationType';
import { UserActionTyp } from '../../../../../../samwebDescription/samweb/userActions/userActions';
import { StartEditingBrickSettings, StopEditingBrickSettings } from '../../../brick-instances/brick-instances.actions';
import {
   AddNewActionToToolbar,
   ChangeActionColorForToolbar,
   ChangeActionIconForToolbar,
   ChangeActionOrderForToolbar,
   ChangeActionTitleForToolbar,
   ChangeActionTypeForToolbarAction,
   ChangeToolbarPresentation,
   ExecuteToolbarAction,
   RemoveActionForToolbar,
   SetToolbarSettings,
   UserActionSettingChanged,
} from './toolbar-settings.actions';

function getDefaultSpecificBrickSetting(): ToolbarSettingRemoteDto {
   return {
      userActions: [],
      toolbarPresentationType: PresentationType.ICON_AND_TEXT.value,
   };
}

export interface ToolbarComponentSettingsStateModel {
   toolbarSettings: { [toolbarId: string]: ToolbarSettingRemoteDto };
}

export interface ToolbarSettingsStateModel {
   toolbarComponentSettings: { [brickInstanceId: string]: ToolbarComponentSettingsStateModel };
   toolbarComponentSettingsEditMode: { [brickInstanceId: string]: ToolbarComponentSettingsStateModel };
}

@State<ToolbarSettingsStateModel>({
   name: 'toolbarSettings',
   defaults: {
      toolbarComponentSettings: {},
      toolbarComponentSettingsEditMode: null,
   },
})
@Injectable()
export class ToolbarSettingsState {
   constructor() {}

   protected static getSettingModelOrDefault(state: ToolbarSettingsStateModel, brickInstanceId: string, toolbarId: string): ToolbarSettingRemoteDto {
      const settingsToUse = state.toolbarComponentSettingsEditMode ?? state.toolbarComponentSettings;
      if (_.isNil(settingsToUse)) {
         return getDefaultSpecificBrickSetting();
      }

      const brickSettingsToUse = settingsToUse[brickInstanceId];
      if (_.isNil(brickSettingsToUse)) {
         return getDefaultSpecificBrickSetting();
      }

      const tbsSettingsToUse = brickSettingsToUse.toolbarSettings;
      if (_.isNil(tbsSettingsToUse)) {
         return getDefaultSpecificBrickSetting();
      }

      const tbs = tbsSettingsToUse[toolbarId];
      if (_.isNil(tbs)) {
         return getDefaultSpecificBrickSetting();
      }

      return tbs;
   }

   @Selector([ToolbarSettingsState])
   public static getToolbarSettingsFn(state) {
      return (brickInstanceId: string, toolbarId: string) => {
         return ToolbarSettingsState.getSettingModelOrDefault(state, brickInstanceId, toolbarId);
      };
   }

   @Selector([ToolbarSettingsState])
   public static getToolbarEditModeSettings(state: ToolbarSettingsStateModel) {
      return (brickInstanceId: string, toolbarId: string) => {
         let settingModel: ToolbarSettingRemoteDto = state.toolbarComponentSettingsEditMode[brickInstanceId]?.toolbarSettings[toolbarId];
         if (_.isNil(settingModel)) {
            settingModel = {
               userActions: [],
               toolbarPresentationType: PresentationType.ICON_AND_TEXT.value,
            };
         }
         return settingModel;
      };
   }

   @Selector([ToolbarSettingsState.getToolbarSettingsFn])
   public static getToolbarActionIdsFn(getToolbarSettingsFn) {
      return (brickInstanceId: string, toolbarId: string) => {
         const model = getToolbarSettingsFn(brickInstanceId, toolbarId);
         return model.userActions.map((x) => x.actionId);
      };
   }

   @Selector([ToolbarSettingsState.getToolbarSettingsFn])
   public static hasToolbarActions(getToolbarSettingsFn) {
      return (brickInstanceId: string, toolbarId: string) => {
         const model = getToolbarSettingsFn(brickInstanceId, toolbarId);
         return model.userActions.map((x) => x.actionId).length > 0;
      };
   }

   @Selector([ToolbarSettingsState.getToolbarSettingsFn])
   public static getToolbarPresentationTypeFn(getToolbarSettingsFn) {
      return (brickInstanceId: string, toolbarId: string) => {
         const model = getToolbarSettingsFn(brickInstanceId, toolbarId);
         let presentationType = null;
         presentationType = model.toolbarPresentationType;
         if (_.isNil(presentationType)) {
            return PresentationType.ICON_AND_TEXT;
         }
         return PresentationType.valueOf(presentationType);
      };
   }

   @Selector([ToolbarSettingsState.getToolbarSettingsFn])
   public static getToolbarActionFn(getToolbarSettingsFn) {
      return (brickInstanceId: string, toolbarId: string, actionId: string) => {
         const settingModel = getToolbarSettingsFn(brickInstanceId, toolbarId);

         return _(settingModel.userActions)
            .filter((x) => x.actionId === actionId)
            .head();
      };
   }

   @Selector([ToolbarSettingsState.getToolbarActionFn])
   public static getActionTypeForUserActionFn(getToolbarActionFn: (brickId, toolbarId, actionId) => UserActionRemoteDto) {
      return (brickId: string, toolbarId: string, actionId: string) => {
         const userAction = getToolbarActionFn(brickId, toolbarId, actionId);
         if (_.isNil(userAction)) {
            return UserActionTyp.UNKNOWN_ACTION;
         }
         const resultValue = _.isEmpty(userAction.userActionType) ? UserActionTyp.UNKNOWN_ACTION : UserActionTyp.find(userAction.userActionType);
         return _.isNil(resultValue) ? UserActionTyp.UNKNOWN_ACTION : resultValue;
      };
   }

   @Selector([ToolbarSettingsState.getToolbarActionFn, ToolbarSettingsState.getActionTypeForUserActionFn])
   public static getActionIconFn(
      getToolbarActionFn: (brickId, toolbarId, actionId) => UserActionRemoteDto,
      getActionTypeForUserActionFn: (brickId, toolbarId, actionId) => UserActionTyp
   ) {
      return (brickId: string, toolbarId: string, actionId: string) => {
         const userAction = getToolbarActionFn(brickId, toolbarId, actionId);
         return _.isNil(userAction) || _.isEmpty(userAction.iconType)
            ? getActionTypeForUserActionFn(brickId, toolbarId, actionId).defaultIcon.iconName
            : userAction.iconType;
      };
   }

   @Selector([ToolbarSettingsState.getToolbarActionFn, ToolbarSettingsState.getActionTypeForUserActionFn])
   public static getActionSettingFn(
      getToolbarActionFn: (brickId, toolbarId, actionId) => UserActionRemoteDto,
      getActionTypeForUserActionFn: (brickId, toolbarId, actionId) => UserActionTyp
   ): (brickId: string, toolbarId: string, actionId: string) => UserActionSettingBaseRemoteDto {
      return (brickId: string, toolbarId: string, actionId: string) => {
         const userAction = getToolbarActionFn(brickId, toolbarId, actionId);
         if (_.isNil(userAction) || _.isNil(userAction.actionSettings)) {
            return getActionTypeForUserActionFn(brickId, toolbarId, actionId).defaultActionSettings;
         }
         return userAction.actionSettings;
      };
   }

   private static updateSettingsModel(state: ToolbarSettingsStateModel, brickInstanceId: string, toolbarId: string, settings: ToolbarSettingRemoteDto) {
      const t = state.toolbarComponentSettings;
      if (_.isNil(t[brickInstanceId])) {
         t[brickInstanceId] = { toolbarSettings: {} };
      }
      t[brickInstanceId].toolbarSettings[toolbarId] = settings;
   }

   private static updateEditSettingsModel(state: ToolbarSettingsStateModel, brickInstanceId: string, toolbarId: string, settings: ToolbarSettingRemoteDto) {
      const t = state.toolbarComponentSettingsEditMode;
      if (_.isNil(t[brickInstanceId])) {
         t[brickInstanceId] = { toolbarSettings: {} };
      }
      t[brickInstanceId].toolbarSettings[toolbarId] = settings;
   }

   @Action(AddNewActionToToolbar)
   @ImmutableContext()
   public addNewActionToToolbar(ctx: StateContext<ToolbarSettingsStateModel>, action: AddNewActionToToolbar) {
      ctx.setState((state) => {
         const chance = new Chance();
         const settingModel = ToolbarSettingsState.getSettingModelOrDefault(state, action.brickInstanceId, action.toolbarId);
         settingModel.userActions = settingModel.userActions ?? [];
         settingModel.userActions.push({
            actionId: chance.string({ length: 5, casing: 'lower', alpha: true, numeric: true }),
         });
         ToolbarSettingsState.updateEditSettingsModel(state, action.brickInstanceId, action.toolbarId, settingModel);
         return state;
      });
   }

   @Action(ChangeToolbarPresentation)
   @ImmutableContext()
   public changeToolbarPresentation(ctx: StateContext<ToolbarSettingsStateModel>, action: ChangeToolbarPresentation) {
      ctx.setState((state) => {
         const settingModel = ToolbarSettingsState.getSettingModelOrDefault(state, action.brickInstanceId, action.toolbarId);
         settingModel.toolbarPresentationType = action.presentationType.value;
         ToolbarSettingsState.updateEditSettingsModel(state, action.brickInstanceId, action.toolbarId, settingModel);
         return state;
      });
   }

   @Action(ChangeActionOrderForToolbar)
   @ImmutableContext()
   public changeActionOrderForToolbar(ctx: StateContext<ToolbarSettingsStateModel>, action: ChangeActionOrderForToolbar) {
      ctx.setState((state) => {
         const settingModel = ToolbarSettingsState.getSettingModelOrDefault(state, action.brickInstanceId, action.toolbarId);
         if (action.newIndex < 0 || action.newIndex > settingModel.userActions.length - 1) {
            return;
         }
         const newOrderedItems = move(settingModel.userActions, action.oldIndex, action.newIndex);
         settingModel.userActions = newOrderedItems;
         ToolbarSettingsState.updateEditSettingsModel(state, action.brickInstanceId, action.toolbarId, settingModel);
         return state;
      });
   }

   @Action(ChangeActionTitleForToolbar)
   @ImmutableContext()
   public changeActionTitleForToolbar(ctx: StateContext<ToolbarSettingsStateModel>, action: ChangeActionTitleForToolbar) {
      ctx.setState((state) => {
         const settingModel = ToolbarSettingsState.getSettingModelOrDefault(state, action.brickInstanceId, action.toolbarId);
         const userAction = _.find(settingModel.userActions, (x) => x.actionId === action.actionId);
         if (_.isNil(userAction)) {
            return;
         }
         userAction.title = action.newTitle;
         ToolbarSettingsState.updateEditSettingsModel(state, action.brickInstanceId, action.toolbarId, settingModel);
         return state;
      });
   }

   @Action(ChangeActionColorForToolbar)
   @ImmutableContext()
   public changeActionColorForToolbar(ctx: StateContext<ToolbarSettingsStateModel>, action: ChangeActionColorForToolbar) {
      ctx.setState((state) => {
         const settingModel = ToolbarSettingsState.getSettingModelOrDefault(state, action.brickInstanceId, action.toolbarId);
         const userAction = _.find(settingModel.userActions, (x) => x.actionId === action.actionId);
         if (_.isNil(userAction)) {
            return;
         }
         userAction.color = action.newColor;
         ToolbarSettingsState.updateEditSettingsModel(state, action.brickInstanceId, action.toolbarId, settingModel);
         return state;
      });
   }

   @Action(ChangeActionIconForToolbar)
   @ImmutableContext()
   public changeActionIconForToolbar(ctx: StateContext<ToolbarSettingsStateModel>, action: ChangeActionIconForToolbar) {
      ctx.setState((state) => {
         const settingModel = ToolbarSettingsState.getSettingModelOrDefault(state, action.brickInstanceId, action.toolbarId);
         const userAction = _.find(settingModel.userActions, (x) => x.actionId === action.actionId);
         if (_.isNil(userAction)) {
            return;
         }
         userAction.iconType = action.icon.iconName;
         ToolbarSettingsState.updateEditSettingsModel(state, action.brickInstanceId, action.toolbarId, settingModel);
         return state;
      });
   }

   @Action(RemoveActionForToolbar)
   @ImmutableContext()
   public removeActionForToolbar(ctx: StateContext<ToolbarSettingsStateModel>, action: RemoveActionForToolbar) {
      ctx.setState((state) => {
         const settingModel = ToolbarSettingsState.getSettingModelOrDefault(state, action.brickInstanceId, action.toolbarId);
         _.remove(settingModel.userActions, (x: UserActionRemoteDto) => x.actionId === action.actionId);
         ToolbarSettingsState.updateEditSettingsModel(state, action.brickInstanceId, action.toolbarId, settingModel);
         return state;
      });
   }

   @Action(ChangeActionTypeForToolbarAction)
   @ImmutableContext()
   public changeActionTypeForToolbarAction(ctx: StateContext<ToolbarSettingsStateModel>, action: ChangeActionTypeForToolbarAction) {
      ctx.setState((state) => {
         const settingModel = ToolbarSettingsState.getSettingModelOrDefault(state, action.brickInstanceId, action.toolbarId);
         const userAction = _.find(settingModel.userActions, (x) => x.actionId === action.actionId);
         if (_.isNil(userAction)) {
            return;
         }
         userAction.userActionType = action.actionTyp.value;
         userAction.actionSettings = action.actionTyp.defaultActionSettings;
         userAction.iconType = action.actionTyp.defaultIcon.iconName;
         ToolbarSettingsState.updateEditSettingsModel(state, action.brickInstanceId, action.toolbarId, settingModel);
         return state;
      });
   }

   @Action(ExecuteToolbarAction)
   @ImmutableContext()
   public executeToolbarAction(ctx: StateContext<ToolbarSettingsStateModel>, action: ExecuteToolbarAction) {
      const userActionSettings = action.store.selectSnapshot(ToolbarSettingsState.getActionSettingFn)(
         action.brickInstanceId,
         action.toolbarId,
         action.actionId
      );
      const userActionTyp = action.store.selectSnapshot(ToolbarSettingsState.getActionTypeForUserActionFn)(
         action.brickInstanceId,
         action.toolbarId,
         action.actionId
      );

      return userActionTyp.executeUserAction$(action.store, userActionSettings as any);
   }

   @Action(UserActionSettingChanged)
   @ImmutableContext()
   public userActionSettingChanged(ctx: StateContext<ToolbarSettingsStateModel>, action: UserActionSettingChanged) {
      ctx.setState((state) => {
         const settingModel = ToolbarSettingsState.getSettingModelOrDefault(state, action.brickInstanceId, action.toolbarId);
         const userAction = _.find(settingModel.userActions, (x) => x.actionId === action.actionId);
         if (_.isNil(userAction)) {
            return;
         }
         userAction.actionSettings = action.actionSetting;
         ToolbarSettingsState.updateEditSettingsModel(state, action.brickInstanceId, action.toolbarId, settingModel);
         return state;
      });
   }

   @Action(StartEditingBrickSettings)
   public enableEditModeForBrickSettings(ctx: StateContext<ToolbarSettingsStateModel>, action: StartEditingBrickSettings) {
      if (!_.isNil(ctx.getState().toolbarComponentSettingsEditMode)) {
         return;
      }
      ctx.setState(
         patch({
            toolbarComponentSettingsEditMode: _.cloneDeep(ctx.getState().toolbarComponentSettings),
         })
      );
   }

   @Action(StopEditingBrickSettings)
   public disableEditModeForBrickSettings(ctx: StateContext<ToolbarSettingsStateModel>, action: StopEditingBrickSettings) {
      ctx.setState(
         patch({
            toolbarComponentSettingsEditMode: null,
         })
      );
   }

   @Action(SetToolbarSettings)
   @ImmutableContext()
   public setToolbarSettings(ctx: StateContext<ToolbarSettingsStateModel>, action: SetToolbarSettings) {
      ctx.setState((state) => {
         ToolbarSettingsState.updateSettingsModel(state, action.brickInstanceId, action.toolbarId, action.toolbarSettings);
         return state;
      });
   }
}
